import React, { FC, useState, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import { reportQueries } from '@/entities/report';
import useMetaCurriData from '@/shared/hooks/report/use-meta-curridata';

interface SelectBtn {
    isSelected: boolean;
    title: string;
    customYn?: boolean;
}

interface AiPlainLanguageProps {
    stntId: string;
    metaIdList?: any;
    onReviewGenerated: (review: string) => void;
    showTextModal: (text: string) => void;
}

const defaultAttitudeList: SelectBtn[] = [
    { isSelected: false, title: '적극적' },
    { isSelected: false, title: '논리적' },
    { isSelected: false, title: '긍정적' },
    { isSelected: false, title: '모범적' },
    { isSelected: false, title: '차분함' },
];

const defaultKeywordList: SelectBtn[] = [
    { isSelected: false, title: '활동' },
    { isSelected: false, title: '소통' },
    { isSelected: false, title: '배려' },
    { isSelected: false, title: '집중' },
    { isSelected: false, title: '창의' },
];

export const AiPlainLanguage: FC<AiPlainLanguageProps> = ({
    stntId,
    metaIdList,
    onReviewGenerated,
    showTextModal,
}) => {
    // 범위(커리큘럼) 관련 상태
    const [curriculumFilters, setCurriculumFilters] = useState<CurriculumProps[]>([]);
    const [curriculumPropsList, setCurriculumPropsList] = useState<CurriculumProps[]>([]);

    // 태도/키워드 관련 상태
    const [attitudeList, setAttitudeList] = useState<SelectBtn[]>(defaultAttitudeList);
    const [keywordList, setKeywordList] = useState<SelectBtn[]>(defaultKeywordList);
    const [selAttitudeList, setSelAttitudeList] = useState<SelectBtn[]>([]);
    const [selKeywordList, setSelKeywordList] = useState<SelectBtn[]>([]);
    const [inputAttitude, setInputAttitude] = useState<string>('');
    const [inputKeyword, setInputKeyword] = useState<string>('');
    const [clearInput, setClearInput] = useState<boolean>(false);

    // 활동 및 수준
    const [activeText, setActiveText] = useState<string>('');
    // 수준 선택: 버튼 클릭 시 내부 state는 "상", "중", "하"로 설정됨.
    const [levelText, setLevelText] = useState<string>('상');

    // 평어 생성 mutation (react-query)
    const overallReviewMutation = reportQueries.review.postOverallReview();

    const scopeText = React.useMemo(() => {
        return curriculumPropsList.map((item) => item.text);
    }, [curriculumPropsList]);

    // 커리큘럼 데이터: useMetaCurriData 훅을 사용하여 가져옴
    const { data } = useMetaCurriData('total-list');
    useEffect(() => {
        if (data) {
            setCurriculumFilters(data.treeData);
        }
    }, [data]);

    // // 커리큘럼 항목 선택 (범위) 핸들러
    // const listClick = (curriculum: CurriculumProps, e?: any) => {
    //     let cpCurriculumList = [...curriculumPropsList];
    //     if (!e || e.target.checked) {
    //         cpCurriculumList.push(curriculum);
    //     } else {
    //         cpCurriculumList = cpCurriculumList.filter((item) => item.id !== curriculum.id);
    //     }
    //     setCurriculumPropsList(cpCurriculumList);

    // };
    const listClick = (curriculum: CurriculumProps, e?: any) => {
        setCurriculumPropsList((prev) => {
            if (e === undefined || e.target.checked) {
                return [...prev, curriculum];
            } else {
                return prev.filter((item) => item.id !== curriculum.id);
            }
        });
    };

    // 태도/키워드 버튼 클릭 (선택/해제)
    const selectClickHandler = (type: 'attitude' | 'keyword', index: number) => {
        if (type === 'attitude') {
            const newList = [...attitudeList];
            newList[index].isSelected = !newList[index].isSelected;
            setAttitudeList(newList);
        } else {
            const newList = [...keywordList];
            newList[index].isSelected = !newList[index].isSelected;
            setKeywordList(newList);
        }
    };

    // 선택된 태도/키워드 해제 핸들러 (직접 입력 항목)
    const unSelectClickHandler = (type: 'attitude' | 'keyword', index: number) => {
        if (type === 'attitude') {
            const newSelList = [...selAttitudeList];
            const newAttList = [...attitudeList];
            const item = newSelList[index];
            const foundIndex = newAttList.findIndex(
                (it) => it.title === item.title && it.customYn === item.customYn
            );
            if (foundIndex > -1) {
                newAttList[foundIndex].isSelected = false;
                setAttitudeList(newAttList);
            }
            newSelList.splice(index, 1);
            setSelAttitudeList(newSelList);
        } else {
            const newSelList = [...selKeywordList];
            const newKeyList = [...keywordList];
            const item = newSelList[index];
            const foundIndex = newKeyList.findIndex(
                (it) => it.title === item.title && it.customYn === item.customYn
            );
            if (foundIndex > -1) {
                newKeyList[foundIndex].isSelected = false;
                setKeywordList(newKeyList);
            }
            newSelList.splice(index, 1);
            setSelKeywordList(newSelList);
        }
    };

    // 입력 필드 변경 핸들러
    const changeText = (
        type: 'attitude' | 'keyword',
        e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        const value = e.target.value;
        if (type === 'attitude') {
            setInputAttitude(value);
        } else {
            setInputKeyword(value);
        }
    };

    // Enter 키 입력 시 직접 입력된 값 추가
    const activeEnter = (
        type: 'attitude' | 'keyword',
        e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        if (e.key === 'Enter') {
            addText(type);
        }
    };

    // 직접 입력값 추가 (최대 5개 제한)
    const addText = (type: 'attitude' | 'keyword') => {
        const newBtn: SelectBtn = { isSelected: false, title: '', customYn: true };
        if (type === 'attitude' && selAttitudeList.length < 5) {
            newBtn.title = inputAttitude;
            if (!selAttitudeList.find((item) => item.title === newBtn.title)) {
                setSelAttitudeList([...selAttitudeList, newBtn]);
            }
            setInputAttitude('');
        } else if (type === 'keyword' && selKeywordList.length < 5) {
            newBtn.title = inputKeyword;
            if (!selKeywordList.find((item) => item.title === newBtn.title)) {
                setSelKeywordList([...selKeywordList, newBtn]);
            }
            setInputKeyword('');
        }
    };

    const triggerClearInput = () => {
        setClearInput(true);
        setTimeout(() => setClearInput(false), 0);
    };

    // AI 평어 생성 API 호출 (mutation 사용)
    const createAiReview = async () => {
        const selectedAttitudes = [
            ...attitudeList.filter((item) => item.isSelected),
            ...selAttitudeList,
        ];
        const selectedKeywords = [
            ...keywordList.filter((item) => item.isSelected),
            ...selKeywordList,
        ];

        if (
            selectedAttitudes.length === 0 ||
            selectedKeywords.length === 0 ||
            activeText.trim().length === 0 ||
            scopeText.length === 0
        ) {
            showTextModal('모든 내역을 입력해주세요.');
            return;
        }

        // API 요청 파라미터 구성 (수준은 "상", "중", "하"로 설정됨)
        const req = {
            scope: scopeText.toString(),
            level: levelText,
            activity: activeText,
            attitudes: selectedAttitudes.map((item) => item.title).toString(),
            abilities: selectedKeywords.map((item) => item.title).toString(),
        };

        try {
            const res = await overallReviewMutation.mutateAsync(req);
            if (res) {
                // 평어 생성 성공 시 상위 콜백을 통해 총평 텍스트를 업데이트함
                onReviewGenerated(res.overall_review);
                showTextModal('평어를 생성했습니다.');

                const tabElement = document.getElementById('review-top');
                if (tabElement) {
                    tabElement.scrollIntoView({
                        behavior: 'instant',
                        block: 'end',
                        inline: 'start',
                    });
                }
            }
        } catch (error) {
            console.error('평어 생성 실패', error);
            showTextModal('평어 생성에 실패했습니다.');
        }
    };

    return <div></div>;
};

export default AiPlainLanguage;
